import React, { useState } from 'react';
import { Edit3, Save } from 'lucide-react';
import { useContentStore } from './hooks/useContentStore';
import { SiteContent } from './types';
import ConfirmationDialog from './ConfirmationDialog';

const ContentManager: React.FC = () => {
  const [content, setContent] = useContentStore<SiteContent>('siteContent', {
    hero: {
      title: 'Your Trusted Team of',
      subtitle: 'Independent Licensed Agents',
      ctaText1: 'Meet Our Team',
      ctaText2: 'Learn More',
      videoTitle: 'Watch Our Team Introduction',
      videoUrl: 'https://www.youtube.com/watch?v=cPQZIrUbzVA&feature=youtu.be'
    },
    about: {
      title: 'Why Choose Our Medicare Team?',
      subtitle: 'We\'re dedicated to simplifying Medicare for you. Our experienced team provides personalized guidance to help you make informed decisions about your healthcare coverage.',
      trustTitle: 'Trusted by Thousands of Medicare Recipients',
      trustDescription: 'Our clients trust us because we put their needs first. We\'re committed to providing honest, transparent guidance that helps you find the best Medicare coverage for your situation.',
      benefits: {
        professional: { title: 'Professional Guidance', description: 'Licensed Medicare specialists with deep knowledge of all plan types and regulations.' },
        personalized: { title: 'Personalized Service', description: 'One-on-one consultations tailored to your specific healthcare needs and budget.' },
        proven: { title: 'Proven Track Record', description: 'Over 5,000 satisfied clients and 15+ years of combined Medicare experience.' },
        compassionate: { title: 'Compassionate Care', description: 'We understand healthcare decisions are personal and provide supportive guidance.' },
        available: { title: 'Always Available', description: 'Available during enrollment periods and ongoing assistance year-round.' },
        noCost: { title: 'No-Cost Consultation', description: 'All our Medicare consultation services are completely free to you.' }
      },
      credentials: {
        licensed: 'Licensed & Bonded',
        certified: 'Medicare Certified',
        accredited: 'BBB Accredited'
      },
      stats: {
        clients: '500+',
        clientsLabel: 'Clients Served',
        experience: '15+',
        experienceLabel: 'Years Experience',
        satisfaction: '100%',
        satisfactionLabel: 'Client Satisfaction'
      },
      valuesTitle: 'Our Values',
      values: {
        integrity: { title: 'Integrity', description: 'Honest, transparent guidance you can trust' },
        service: { title: 'Service', description: 'Putting your needs first, always' },
        excellence: { title: 'Excellence', description: 'Delivering the highest quality service' },
        availability: { title: 'Availability', description: 'Here when you need us most' }
      }
    },
    agents: {
      title: 'Meet Our Licensed Team',
      subtitle: 'Our dedicated Medicare specialists are here to help you navigate your healthcare options. Click on any team member to connect directly with them.',
      readyTitle: 'Ready to Get Started?',
      readySubtitle: 'Click on any team member above to schedule a free consultation or get in touch directly.',
      readyFooter: 'All consultations are completely free and there\'s no obligation to enroll.'
    },
    navigation: {
      logoText: 'BBHA Insurance Services',
      menuItems: [
        { label: 'Home', id: 'hero' },
        { label: 'About', id: 'about' },
        { label: 'Our Team', id: 'agents' },
        { label: 'Company', id: 'about-us' }
      ]
    },
    company: {
      name: 'BBHA Insurance Services',
      address: '123 Main Street, Anytown, CA 12345',
      phone: '(555) 123-4567',
      email: 'info@bbhainsurance.com',
      description: 'Professional Medicare guidance from licensed independent agents.',
      hours: {
        weekdays: '8:00 AM - 6:00 PM',
        saturday: '9:00 AM - 3:00 PM',
        sunday: 'Closed',
        extended: 'Extended Hours available during enrollment periods'
      }
    }
  });

  const [activeSection, setActiveSection] = useState('hero');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState('');

  const handleInputChange = (section: keyof SiteContent, field: string, value: string) => {
    setContent(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const sections = [
    { id: 'hero', label: 'Top of Page' },
    { id: 'about', label: 'About Us' },
    { id: 'agents', label: 'Team Section' },
    { id: 'company', label: 'Contact Details' }
  ];

  const renderSection = () => {
    switch (activeSection) {
      case 'hero':
        return (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
                <Edit3 size={24} />
                <span>Top of Page</span>
              </h2>
              <button
                onClick={() => {
                  setContent(prev => ({ ...prev }));
                  setConfirmationMessage('Your top-of-page text has been saved.');
                  setShowConfirmation(true);
                }}
                className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
              >
                <Save size={16} />
                <span>Save Changes</span>
              </button>
            </div>
            
            <div className="grid gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Main Title
                </label>
                <input
                  type="text"
                  value={content.hero.title}
                  onChange={(e) => handleInputChange('hero', 'title', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subtitle
                </label>
                <input
                  type="text"
                  value={content.hero.subtitle}
                  onChange={(e) => handleInputChange('hero', 'subtitle', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Primary CTA Button
                  </label>
                  <input
                    type="text"
                    value={content.hero.ctaText1}
                    onChange={(e) => handleInputChange('hero', 'ctaText1', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Secondary CTA Button
                  </label>
                  <input
                    type="text"
                    value={content.hero.ctaText2}
                    onChange={(e) => handleInputChange('hero', 'ctaText2', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Video Title
                </label>
                <input
                  type="text"
                  value={content.hero.videoTitle}
                  onChange={(e) => handleInputChange('hero', 'videoTitle', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Video URL
                </label>
                <input
                  type="url"
                  value={content.hero.videoUrl}
                  onChange={(e) => handleInputChange('hero', 'videoUrl', e.target.value)}
                  placeholder="https://www.youtube.com/watch?v=..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Enter a YouTube video URL. The thumbnail will be automatically loaded.
                </p>
              </div>
            </div>
          </div>
        );
        
      case 'about':
        return (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
                <Edit3 size={24} />
                <span>About Us</span>
              </h2>
              <button
                onClick={() => {
                  setContent(prev => ({ ...prev }));
                  setConfirmationMessage('Your About Us changes have been saved.');
                  setShowConfirmation(true);
                }}
                className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
              >
                <Save size={16} />
                <span>Save Changes</span>
              </button>
            </div>
            
            <div className="grid gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Section Title
                </label>
                <input
                  type="text"
                  value={content.about.title}
                  onChange={(e) => handleInputChange('about', 'title', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Section Subtitle
                </label>
                <textarea
                  value={content.about.subtitle}
                  onChange={(e) => handleInputChange('about', 'subtitle', e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Trust Section Title
                </label>
                <input
                  type="text"
                  value={content.about.trustTitle}
                  onChange={(e) => handleInputChange('about', 'trustTitle', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Trust Section Description
                </label>
                <textarea
                  value={content.about.trustDescription}
                  onChange={(e) => handleInputChange('about', 'trustDescription', e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Benefits Section</h3>
                
                <div className="grid gap-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Professional Guidance - Title
                      </label>
                      <input
                        type="text"
                        value={content.about.benefits?.professional?.title || ''}
                        onChange={(e) => {
                          const newContent = { ...content };
                          if (!newContent.about.benefits) newContent.about.benefits = {} as any;
                          if (!newContent.about.benefits.professional) newContent.about.benefits.professional = {} as any;
                          newContent.about.benefits.professional.title = e.target.value;
                          setContent(newContent);
                        }}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Professional Guidance - Description
                      </label>
                      <textarea
                        value={content.about.benefits?.professional?.description || ''}
                        onChange={(e) => {
                          const newContent = { ...content };
                          if (!newContent.about.benefits) newContent.about.benefits = {} as any;
                          if (!newContent.about.benefits.professional) newContent.about.benefits.professional = {} as any;
                          newContent.about.benefits.professional.description = e.target.value;
                          setContent(newContent);
                        }}
                        rows={2}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Personalized Service - Title
                      </label>
                      <input
                        type="text"
                        value={content.about.benefits?.personalized?.title || ''}
                        onChange={(e) => {
                          const newContent = { ...content };
                          if (!newContent.about.benefits) newContent.about.benefits = {} as any;
                          if (!newContent.about.benefits.personalized) newContent.about.benefits.personalized = {} as any;
                          newContent.about.benefits.personalized.title = e.target.value;
                          setContent(newContent);
                        }}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Personalized Service - Description
                      </label>
                      <textarea
                        value={content.about.benefits?.personalized?.description || ''}
                        onChange={(e) => {
                          const newContent = { ...content };
                          if (!newContent.about.benefits) newContent.about.benefits = {} as any;
                          if (!newContent.about.benefits.personalized) newContent.about.benefits.personalized = {} as any;
                          newContent.about.benefits.personalized.description = e.target.value;
                          setContent(newContent);
                        }}
                        rows={2}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Proven Track Record - Title
                      </label>
                      <input
                        type="text"
                        value={content.about.benefits?.proven?.title || ''}
                        onChange={(e) => {
                          const newContent = { ...content };
                          if (!newContent.about.benefits) newContent.about.benefits = {} as any;
                          if (!newContent.about.benefits.proven) newContent.about.benefits.proven = {} as any;
                          newContent.about.benefits.proven.title = e.target.value;
                          setContent(newContent);
                        }}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Proven Track Record - Description
                      </label>
                      <textarea
                        value={content.about.benefits?.proven?.description || ''}
                        onChange={(e) => {
                          const newContent = { ...content };
                          if (!newContent.about.benefits) newContent.about.benefits = {} as any;
                          if (!newContent.about.benefits.proven) newContent.about.benefits.proven = {} as any;
                          newContent.about.benefits.proven.description = e.target.value;
                          setContent(newContent);
                        }}
                        rows={2}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Compassionate Care - Title
                      </label>
                      <input
                        type="text"
                        value={content.about.benefits?.compassionate?.title || ''}
                        onChange={(e) => {
                          const newContent = { ...content };
                          if (!newContent.about.benefits) newContent.about.benefits = {} as any;
                          if (!newContent.about.benefits.compassionate) newContent.about.benefits.compassionate = {} as any;
                          newContent.about.benefits.compassionate.title = e.target.value;
                          setContent(newContent);
                        }}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Compassionate Care - Description
                      </label>
                      <textarea
                        value={content.about.benefits?.compassionate?.description || ''}
                        onChange={(e) => {
                          const newContent = { ...content };
                          if (!newContent.about.benefits) newContent.about.benefits = {} as any;
                          if (!newContent.about.benefits.compassionate) newContent.about.benefits.compassionate = {} as any;
                          newContent.about.benefits.compassionate.description = e.target.value;
                          setContent(newContent);
                        }}
                        rows={2}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Always Available - Title
                      </label>
                      <input
                        type="text"
                        value={content.about.benefits?.available?.title || ''}
                        onChange={(e) => {
                          const newContent = { ...content };
                          if (!newContent.about.benefits) newContent.about.benefits = {} as any;
                          if (!newContent.about.benefits.available) newContent.about.benefits.available = {} as any;
                          newContent.about.benefits.available.title = e.target.value;
                          setContent(newContent);
                        }}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Always Available - Description
                      </label>
                      <textarea
                        value={content.about.benefits?.available?.description || ''}
                        onChange={(e) => {
                          const newContent = { ...content };
                          if (!newContent.about.benefits) newContent.about.benefits = {} as any;
                          if (!newContent.about.benefits.available) newContent.about.benefits.available = {} as any;
                          newContent.about.benefits.available.description = e.target.value;
                          setContent(newContent);
                        }}
                        rows={2}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        No-Cost Consultation - Title
                      </label>
                      <input
                        type="text"
                        value={content.about.benefits?.noCost?.title || ''}
                        onChange={(e) => {
                          const newContent = { ...content };
                          if (!newContent.about.benefits) newContent.about.benefits = {} as any;
                          if (!newContent.about.benefits.noCost) newContent.about.benefits.noCost = {} as any;
                          newContent.about.benefits.noCost.title = e.target.value;
                          setContent(newContent);
                        }}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        No-Cost Consultation - Description
                      </label>
                      <textarea
                        value={content.about.benefits?.noCost?.description || ''}
                        onChange={(e) => {
                          const newContent = { ...content };
                          if (!newContent.about.benefits) newContent.about.benefits = {} as any;
                          if (!newContent.about.benefits.noCost) newContent.about.benefits.noCost = {} as any;
                          newContent.about.benefits.noCost.description = e.target.value;
                          setContent(newContent);
                        }}
                        rows={2}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  
                  <div className="border-t pt-4 mt-4">
                    <h4 className="text-md font-semibold text-gray-900 mb-3">Credentials</h4>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Licensed Badge
                        </label>
                        <input
                          type="text"
                          value={content.about.credentials?.licensed || ''}
                          onChange={(e) => {
                            const newContent = { ...content };
                            if (!newContent.about.credentials) newContent.about.credentials = {} as any;
                            newContent.about.credentials.licensed = e.target.value;
                            setContent(newContent);
                          }}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Certified Badge
                        </label>
                        <input
                          type="text"
                          value={content.about.credentials?.certified || ''}
                          onChange={(e) => {
                            const newContent = { ...content };
                            if (!newContent.about.credentials) newContent.about.credentials = {} as any;
                            newContent.about.credentials.certified = e.target.value;
                            setContent(newContent);
                          }}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Accredited Badge
                        </label>
                        <input
                          type="text"
                          value={content.about.credentials?.accredited || ''}
                          onChange={(e) => {
                            const newContent = { ...content };
                            if (!newContent.about.credentials) newContent.about.credentials = {} as any;
                            newContent.about.credentials.accredited = e.target.value;
                            setContent(newContent);
                          }}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="border-t pt-4 mt-4">
                    <h4 className="text-md font-semibold text-gray-900 mb-3">Statistics</h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Clients Number
                        </label>
                        <input
                          type="text"
                          value={content.about.stats?.clients || ''}
                          onChange={(e) => {
                            const newContent = { ...content };
                            if (!newContent.about.stats) newContent.about.stats = {} as any;
                            newContent.about.stats.clients = e.target.value;
                            setContent(newContent);
                          }}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Clients Label
                        </label>
                        <input
                          type="text"
                          value={content.about.stats?.clientsLabel || ''}
                          onChange={(e) => {
                            const newContent = { ...content };
                            if (!newContent.about.stats) newContent.about.stats = {} as any;
                            newContent.about.stats.clientsLabel = e.target.value;
                            setContent(newContent);
                          }}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Experience Number
                        </label>
                        <input
                          type="text"
                          value={content.about.stats?.experience || ''}
                          onChange={(e) => {
                            const newContent = { ...content };
                            if (!newContent.about.stats) newContent.about.stats = {} as any;
                            newContent.about.stats.experience = e.target.value;
                            setContent(newContent);
                          }}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Experience Label
                        </label>
                        <input
                          type="text"
                          value={content.about.stats?.experienceLabel || ''}
                          onChange={(e) => {
                            const newContent = { ...content };
                            if (!newContent.about.stats) newContent.about.stats = {} as any;
                            newContent.about.stats.experienceLabel = e.target.value;
                            setContent(newContent);
                          }}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Satisfaction Number
                        </label>
                        <input
                          type="text"
                          value={content.about.stats?.satisfaction || ''}
                          onChange={(e) => {
                            const newContent = { ...content };
                            if (!newContent.about.stats) newContent.about.stats = {} as any;
                            newContent.about.stats.satisfaction = e.target.value;
                            setContent(newContent);
                          }}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Satisfaction Label
                        </label>
                        <input
                          type="text"
                          value={content.about.stats?.satisfactionLabel || ''}
                          onChange={(e) => {
                            const newContent = { ...content };
                            if (!newContent.about.stats) newContent.about.stats = {} as any;
                            newContent.about.stats.satisfactionLabel = e.target.value;
                            setContent(newContent);
                          }}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="border-t pt-4 mt-4">
                    <h4 className="text-md font-semibold text-gray-900 mb-3">Our Values</h4>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Values Section Title
                      </label>
                      <input
                        type="text"
                        value={content.about.valuesTitle || ''}
                        onChange={(e) => {
                          const newContent = { ...content };
                          newContent.about.valuesTitle = e.target.value;
                          setContent(newContent);
                        }}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Integrity - Title
                        </label>
                        <input
                          type="text"
                          value={content.about.values?.integrity?.title || ''}
                          onChange={(e) => {
                            const newContent = { ...content };
                            if (!newContent.about.values) newContent.about.values = {} as any;
                            if (!newContent.about.values.integrity) newContent.about.values.integrity = {} as any;
                            newContent.about.values.integrity.title = e.target.value;
                            setContent(newContent);
                          }}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Integrity - Description
                        </label>
                        <input
                          type="text"
                          value={content.about.values?.integrity?.description || ''}
                          onChange={(e) => {
                            const newContent = { ...content };
                            if (!newContent.about.values) newContent.about.values = {} as any;
                            if (!newContent.about.values.integrity) newContent.about.values.integrity = {} as any;
                            newContent.about.values.integrity.description = e.target.value;
                            setContent(newContent);
                          }}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Service - Title
                        </label>
                        <input
                          type="text"
                          value={content.about.values?.service?.title || ''}
                          onChange={(e) => {
                            const newContent = { ...content };
                            if (!newContent.about.values) newContent.about.values = {} as any;
                            if (!newContent.about.values.service) newContent.about.values.service = {} as any;
                            newContent.about.values.service.title = e.target.value;
                            setContent(newContent);
                          }}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Service - Description
                        </label>
                        <input
                          type="text"
                          value={content.about.values?.service?.description || ''}
                          onChange={(e) => {
                            const newContent = { ...content };
                            if (!newContent.about.values) newContent.about.values = {} as any;
                            if (!newContent.about.values.service) newContent.about.values.service = {} as any;
                            newContent.about.values.service.description = e.target.value;
                            setContent(newContent);
                          }}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Excellence - Title
                        </label>
                        <input
                          type="text"
                          value={content.about.values?.excellence?.title || ''}
                          onChange={(e) => {
                            const newContent = { ...content };
                            if (!newContent.about.values) newContent.about.values = {} as any;
                            if (!newContent.about.values.excellence) newContent.about.values.excellence = {} as any;
                            newContent.about.values.excellence.title = e.target.value;
                            setContent(newContent);
                          }}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Excellence - Description
                        </label>
                        <input
                          type="text"
                          value={content.about.values?.excellence?.description || ''}
                          onChange={(e) => {
                            const newContent = { ...content };
                            if (!newContent.about.values) newContent.about.values = {} as any;
                            if (!newContent.about.values.excellence) newContent.about.values.excellence = {} as any;
                            newContent.about.values.excellence.description = e.target.value;
                            setContent(newContent);
                          }}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Availability - Title
                        </label>
                        <input
                          type="text"
                          value={content.about.values?.availability?.title || ''}
                          onChange={(e) => {
                            const newContent = { ...content };
                            if (!newContent.about.values) newContent.about.values = {} as any;
                            if (!newContent.about.values.availability) newContent.about.values.availability = {} as any;
                            newContent.about.values.availability.title = e.target.value;
                            setContent(newContent);
                          }}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Availability - Description
                        </label>
                        <input
                          type="text"
                          value={content.about.values?.availability?.description || ''}
                          onChange={(e) => {
                            const newContent = { ...content };
                            if (!newContent.about.values) newContent.about.values = {} as any;
                            if (!newContent.about.values.availability) newContent.about.values.availability = {} as any;
                            newContent.about.values.availability.description = e.target.value;
                            setContent(newContent);
                          }}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
        
      case 'agents':
        return (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
                <Edit3 size={24} />
                <span>Team Section</span>
              </h2>
              <button
                onClick={() => {
                  setContent(prev => ({ ...prev }));
                  setConfirmationMessage('Your team-section changes have been saved.');
                  setShowConfirmation(true);
                }}
                className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
              >
                <Save size={16} />
                <span>Save Changes</span>
              </button>
            </div>
            
            <div className="grid gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Section Title
                </label>
                <input
                  type="text"
                  value={content.agents?.title || ''}
                  onChange={(e) => handleInputChange('agents', 'title', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Section Subtitle
                </label>
                <textarea
                  value={content.agents?.subtitle || ''}
                  onChange={(e) => handleInputChange('agents', 'subtitle', e.target.value)}
                  rows={2}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ready Section Title
                </label>
                <input
                  type="text"
                  value={content.agents?.readyTitle || ''}
                  onChange={(e) => handleInputChange('agents', 'readyTitle', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ready Section Subtitle
                </label>
                <textarea
                  value={content.agents?.readySubtitle || ''}
                  onChange={(e) => handleInputChange('agents', 'readySubtitle', e.target.value)}
                  rows={2}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ready Section Footer
                </label>
                <input
                  type="text"
                  value={content.agents?.readyFooter || ''}
                  onChange={(e) => handleInputChange('agents', 'readyFooter', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        );
        
      case 'company':
        return (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
                <Edit3 size={24} />
                <span>Company Information</span>
              </h2>
              <button
                onClick={() => {
                  setContent(prev => ({ ...prev }));
                  setConfirmationMessage('Your contact details have been saved.');
                  setShowConfirmation(true);
                }}
                className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
              >
                <Save size={16} />
                <span>Save Changes</span>
              </button>
            </div>
            
            <div className="grid gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Name
                </label>
                <input
                  type="text"
                  value={content.company.name}
                  onChange={(e) => handleInputChange('company', 'name', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="text"
                    value={content.company.phone}
                    onChange={(e) => handleInputChange('company', 'phone', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={content.company.email}
                    onChange={(e) => handleInputChange('company', 'email', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Address
                </label>
                <input
                  type="text"
                  value={content.company.address}
                  onChange={(e) => handleInputChange('company', 'address', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Description
                </label>
                <textarea
                  value={content.company.description}
                  onChange={(e) => handleInputChange('company', 'description', e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Office Hours</h3>
                
                <div className="grid gap-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Monday - Friday
                      </label>
                      <input
                        type="text"
                        value={content.company.hours?.weekdays || ''}
                        onChange={(e) => {
                          const newContent = { ...content };
                          if (!newContent.company.hours) newContent.company.hours = {} as any;
                          newContent.company.hours.weekdays = e.target.value;
                          setContent(newContent);
                        }}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Saturday
                      </label>
                      <input
                        type="text"
                        value={content.company.hours?.saturday || ''}
                        onChange={(e) => {
                          const newContent = { ...content };
                          if (!newContent.company.hours) newContent.company.hours = {} as any;
                          newContent.company.hours.saturday = e.target.value;
                          setContent(newContent);
                        }}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Sunday
                      </label>
                      <input
                        type="text"
                        value={content.company.hours?.sunday || ''}
                        onChange={(e) => {
                          const newContent = { ...content };
                          if (!newContent.company.hours) newContent.company.hours = {} as any;
                          newContent.company.hours.sunday = e.target.value;
                          setContent(newContent);
                        }}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Extended Hours Note
                      </label>
                      <input
                        type="text"
                        value={content.company.hours?.extended || ''}
                        onChange={(e) => {
                          const newContent = { ...content };
                          if (!newContent.company.hours) newContent.company.hours = {} as any;
                          newContent.company.hours.extended = e.target.value;
                          setContent(newContent);
                        }}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <div className="p-6">
      {/* Header with actions */}
      <div className="flex justify-between items-center mb-8 pb-4 border-b border-gray-200">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Website Text</h1>
          <p className="text-gray-600 mt-1">Choose a section below to update the words shown on your website</p>
        </div>
        
        <div className="flex items-center space-x-3">
        </div>
      </div>

      <div className="grid lg:grid-cols-4 gap-8">
        {/* Section Navigation */}
        <div className="lg:col-span-1">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Website Sections</h3>
          <nav className="space-y-2">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`w-full text-left px-4 py-3 rounded-lg transition-colors duration-200 ${
                  activeSection === section.id
                    ? 'bg-blue-100 text-blue-700 border border-blue-200'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                {section.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Content Editor */}
        <div className="lg:col-span-3">
          {renderSection()}
        </div>
      </div>
      
      <ConfirmationDialog
        isOpen={showConfirmation}
        title="Changes Saved"
        message={confirmationMessage}
        onClose={() => setShowConfirmation(false)}
      />
    </div>
  );
};

export default ContentManager;
